import { useEffect, useRef, useState } from 'react';

import { useEvent, useLazyCommand } from '@soap/modules';

import { UnitCard } from './InProgressUnitsPanel/UnitCard';

import { InProgressUnitsPanelProps } from './InProgressUnitsPanel/types';

import { Play } from 'lucide-react';

import { PlanNoStudentsEmptyState } from './LessonsPage/components/PlanNoStudentsEmptyState';

import { lessonLoadingStateKey, normalizeIdPart } from './InProgressUnitsPanel/loadingStateKeys';



export function InProgressUnitsPanel({

    units,

    viewState,

    viewStates,

    onCreateAssessment,

    onRefresh,

    students,

    isStudentLoading,

    passFailThreshold = null,

    lessonUnitFocus = null,

    onConsumedLessonUnitFocus,

    onSessionViewTitleChange

}: InProgressUnitsPanelProps) {

    const [loadingStates, setLoadingStates] = useState<Set<string>>(new Set());

    const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());

    const [touchedUnits, setTouchedUnits] = useState<Set<string>>(new Set());

    const [sessionView, setSessionView] = useState<{

        unitId: string;

        lessonId: string;

    } | null>(null);



    const [sendMarkUnitTouchedCommand] = useLazyCommand('C281v1_MarkUnitTouched');



    const toggleUnit = (unitId: string) => {

        setExpandedUnits(prev => {

            const wasExpanded = prev.has(unitId);

            if (wasExpanded) {

                return new Set();

            }

            if (!touchedUnits.has(unitId)) {

                const unit = units.find(u => u.e251_UnitId === unitId);

                if (unit && !unit.e251_Touched) {

                    setTouchedUnits(prevTouch => new Set(prevTouch).add(unitId));

                    sendMarkUnitTouchedCommand({ C281_UnitId: unitId });

                }

            }

            return new Set([unitId]);

        });

    };



    useEffect(() => {

        if (!sessionView) return;

        const unit = units.find(

            u => normalizeIdPart(u.e251_UnitId) === normalizeIdPart(sessionView.unitId)

        );

        if (!unit) {

            setSessionView(null);

            return;

        }

        const lesson = (unit.e251_Lessons || []).find(

            l => normalizeIdPart(l.e251_LessonId) === normalizeIdPart(sessionView.lessonId)

        );

        if (!lesson) {

            setSessionView(null);

        }

    }, [units, sessionView]);



    const onSessionViewTitleChangeRef = useRef(onSessionViewTitleChange);

    onSessionViewTitleChangeRef.current = onSessionViewTitleChange;



    useEffect(() => {

        if (sessionView == null) {

            onSessionViewTitleChangeRef.current?.(null);

            return;

        }

        const unit = units.find(

            u => normalizeIdPart(u.e251_UnitId) === normalizeIdPart(sessionView.unitId)

        );

        const lesson = (unit?.e251_Lessons || []).find(

            l => normalizeIdPart(l.e251_LessonId) === normalizeIdPart(sessionView.lessonId)

        );

        const title = (lesson?.e251_LessonTitle || '').trim() || 'Lesson';

        onSessionViewTitleChangeRef.current?.(title);

    }, [sessionView, units]);



    useEffect(() => {

        if (sessionView == null) return;

        window.scrollTo({ top: 0, behavior: 'smooth' });

    }, [sessionView]);



    useEffect(() => {

        return () => {

            onSessionViewTitleChangeRef.current?.(null);

        };

    }, []);



    useEffect(() => {

        if (!lessonUnitFocus?.unitId || !onConsumedLessonUnitFocus) return;



        const unitId = lessonUnitFocus.unitId;

        const appearsInUi = units.some(u => u.e251_UnitId === unitId);

        if (!appearsInUi) return;



        setExpandedUnits(new Set([unitId]));



        const handle = window.setTimeout(() => {

            onConsumedLessonUnitFocus();

        }, 0);



        return () => window.clearTimeout(handle);

    }, [lessonUnitFocus, units, onConsumedLessonUnitFocus]);



    useEvent({

        eventName: 'E258v1_LessonStarted',

        onEventReceived: (event: any) => {

            const ev = event as Record<string, unknown>;

            const key = lessonLoadingStateKey(

                String(ev.e258_UnitId ?? ev.E258_UnitId ?? ''),

                String(ev.e258_LessonId ?? ev.E258_LessonId ?? '')

            );

            setLoadingStates(prev => {

                const next = new Set(prev);

                next.delete(key);

                return next;

            });

        }

    });



    useEvent({

        eventName: 'E318v1_LessonSessionCompleted',

        onEventReceived: (event: any) => {

            const ev = event as Record<string, unknown>;

            const key = lessonLoadingStateKey(

                String(ev.e318_UnitId ?? ev.E318_UnitId ?? ''),

                String(ev.e318_LessonId ?? ev.E318_LessonId ?? '')

            );

            setLoadingStates(prev => {

                const next = new Set(prev);

                next.delete(key);

                return next;

            });

        }

    });



    useEvent({

        eventName: 'E326v1_LessonCustomisedAtDelivery',

        onEventReceived: (event: any) => {

            const ev = event as Record<string, unknown>;

            const key = lessonLoadingStateKey(

                String(ev.e326_UnitId ?? ev.E326_UnitId ?? ''),

                String(ev.e326_LessonId ?? ev.E326_LessonId ?? '')

            );

            setLoadingStates(prev => {

                const next = new Set(prev);

                next.delete(key);

                return next;

            });

        }

    });



    useEvent({

        eventName: 'E261v1_LessonFinished',

        onEventReceived: (event: any) => {

            const ev = event as Record<string, unknown>;

            const key = lessonLoadingStateKey(

                String(ev.e261_UnitId ?? ev.E261_UnitId ?? ''),

                String(ev.e261_LessonId ?? ev.E261_LessonId ?? '')

            );

            setLoadingStates(prev => {

                const next = new Set(prev);

                next.delete(key);

                return next;

            });

        }

    });



    useEvent({

        eventName: 'E255v1_UnitDeleted',

        onEventReceived: (event: any) => {

            const key = event.e255_UnitId;

            setLoadingStates(prev => {

                const next = new Set(prev);

                next.delete(key);

                return next;

            });

        }

    });



    switch (viewState) {

        case viewStates.loading:

            if (!isStudentLoading && students.length === 0) {

                return (

                    <PlanNoStudentsEmptyState description="You need to create a student before you can view units in progress." />

                );

            }

            return (

                <div className="flex items-center justify-center py-12">

                    <div className="flex flex-col items-center gap-3">

                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>

                        <span className="text-gray-600">Loading units...</span>

                    </div>

                </div>

            );



        case viewStates.ready:

            if (units.length === 0) {

                return (

                    <div className="text-center py-12">

                        <Play className="mx-auto h-12 w-12 text-gray-400" />

                        <h3 className="mt-2 text-sm font-medium text-gray-900">No units in progress</h3>

                        <p className="mt-1 text-sm text-gray-500">

                            Approve a unit from the Approve Units tab to get started

                        </p>

                    </div>

                );

            }



            return (

                <div className="space-y-4">

                    {units.map(unit => {

                        const uid = unit.e251_UnitId || '';

                        const unitIsSessionViewActive =

                            sessionView != null &&

                            normalizeIdPart(uid) === normalizeIdPart(sessionView.unitId);

                        const hidePeerUnit = sessionView != null && !unitIsSessionViewActive;

                        return (

                            <div key={unit.e251_UnitId} hidden={hidePeerUnit}>

                                <UnitCard

                                    unit={unit}

                                    expandedUnits={expandedUnits}

                                    loadingStates={loadingStates}

                                    onToggleUnit={toggleUnit}

                                    onCreateAssessment={onCreateAssessment}

                                    setLoadingStates={setLoadingStates}

                                    onRefresh={onRefresh}

                                    passFailThreshold={passFailThreshold}

                                    sessionViewLessonId={unitIsSessionViewActive ? sessionView!.lessonId : null}

                                    onSessionViewChange={(lessonId, focused) => {

                                        setSessionView(prev => {

                                            if (focused) {

                                                return { unitId: uid, lessonId };

                                            }

                                            if (

                                                prev &&

                                                normalizeIdPart(prev.unitId) === normalizeIdPart(uid) &&

                                                normalizeIdPart(prev.lessonId) === normalizeIdPart(lessonId)

                                            ) {

                                                return null;

                                            }

                                            return prev;

                                        });

                                    }}

                                />

                            </div>

                        );

                    })}

                </div>

            );



        default:

            return null;

    }

}


